﻿export type PanelCollapseState = 'open' | 'collapsed';

export interface PanelCollapseOptions {
    /** localStorage key unique to the panel, e.g. 'panel-collapse:collections-picker'. */
    readonly storageKey: string;
    /** The panel container that receives the collapsed layout. */
    readonly panel: HTMLElement;
    /** The injected Windows-style toggle button. */
    readonly button: HTMLButtonElement;
    /** aria-label/title shown while the panel is open ("Minimize ..."). */
    readonly collapseLabel: string;
    /** aria-label/title shown while the panel is collapsed ("Restore ..."). */
    readonly expandLabel: string;
}

let adminSessionResult: Promise<boolean> | null = null;

/**
 * Queries the admin session endpoint and reports whether the current visitor is a signed-in administrator.
 * The result is cached so several panels share one request per page load, and every failure fails closed
 * (the controls are never shown to non-administrators).
 *
 * @returns A promise resolving to true only for a confirmed signed-in administrator.
 */
function isAdminSession(): Promise<boolean> {
    adminSessionResult ??= fetch('/api/admin/auth/session', {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
    })
        .then(async response => {
            if (!response.ok) return false;
            const payload = (await response.json()) as { authenticated?: boolean };
            return payload.authenticated === true;
        })
        .catch(() => false);
    return adminSessionResult;
}

/**
 * Builds the Windows-like minimize control. The open state shows a box icon and the collapsed state a bar
 * icon; CSS switches between the two using the aria-expanded state maintained by initPanelCollapse.
 *
 * @param collapseLabel - Accessible label shown while the panel is open.
 * @param expandLabel - Accessible label shown while the panel is collapsed.
 * @returns The configured button, ready to be handed to initPanelCollapse.
 */
export function createPanelCollapseButton(collapseLabel: string, expandLabel: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'panel-collapse-button';
    button.setAttribute('aria-expanded', 'true');
    button.setAttribute('aria-label', collapseLabel);
    button.title = collapseLabel;

    const openIcon = document.createElement('span');
    openIcon.className = 'panel-collapse-icon panel-collapse-icon--open';
    openIcon.setAttribute('aria-hidden', 'true');
    const closedIcon = document.createElement('span');
    closedIcon.className = 'panel-collapse-icon panel-collapse-icon--closed';
    closedIcon.setAttribute('aria-hidden', 'true');
    button.append(openIcon, closedIcon);
    return button;
}

/** Reads the persisted panel state, accepting only the two known values and defaulting to open. */
function readStoredState(storageKey: string): PanelCollapseState {
    try {
        const stored = localStorage.getItem(storageKey);
        if (stored === 'open' || stored === 'collapsed') return stored;
    } catch {
        // Storage can be unavailable; the default open state still works for this page view.
    }
    return 'open';
}

/** Applies the visual and accessible state of one panel and its toggle button. */
function applyState(options: PanelCollapseOptions, state: PanelCollapseState): void {
    const isCollapsed = state === 'collapsed';
    options.panel.classList.toggle('panel-collapsed', isCollapsed);
    options.button.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
    options.button.setAttribute('aria-label', isCollapsed ? options.expandLabel : options.collapseLabel);
    options.button.title = isCollapsed ? options.expandLabel : options.collapseLabel;
}

/**
 * Initializes the collapse control for one panel. Signed-in administrators get the button with their
 * persisted open/collapsed state restored; everyone else sees nothing and no DOM changes are made.
 *
 * @param options - The panel, its storage key, the injected button, and the accessible labels.
 */
export async function initPanelCollapse(options: PanelCollapseOptions): Promise<void> {
    if (!(await isAdminSession())) return;

    const state = readStoredState(options.storageKey);
    options.panel.append(options.button);
    applyState(options, state);

    options.button.addEventListener('click', () => {
        const nextState = options.panel.classList.contains('panel-collapsed') ? 'open' : 'collapsed';
        applyState(options, nextState);
        try {
            localStorage.setItem(options.storageKey, nextState);
        } catch {
            // Persistence is best-effort; the toggle still works for the rest of this page view.
        }
    });
}
